import { useState, useRef } from "react";
import { useLocation } from "wouter";
import { Camera, Check, ChevronRight, Loader2, Sprout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { queryClient } from "@/lib/queryClient";

const SELLER_TYPES = [
  { value: "grower", label: "Grower", desc: "Vegetables, fruit, herbs, flowers" },
  { value: "maker", label: "Maker", desc: "Baked goods, preserves, honey, candles" },
  { value: "farmstand", label: "Farm Stand", desc: "A mix of grown + made products" },
  { value: "mixed", label: "Mixed", desc: "Multiple product categories" },
] as const;

type SellerType = (typeof SELLER_TYPES)[number]["value"];

const INTENT_SUGGESTIONS = [
  "Eggs",
  "Honey",
  "Tomatoes",
  "Greens",
  "Herbs",
  "Bread",
  "Jam",
  "Maple syrup",
  "Beef",
  "Pork",
  "Chicken",
  "Flowers",
  "Candles",
  "Soap",
  "Baked goods",
];

export default function SellerSetup() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [showPhotoPrompt, setShowPhotoPrompt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [sellerType, setSellerType] = useState<SellerType | "">(
    (user?.sellerType as SellerType) ?? "",
  );
  const [bio, setBio] = useState((user as any)?.bio ?? "");
  const [profileImageUrl, setProfileImageUrl] = useState(user?.profileImageUrl ?? "");
  const [addressLine1, setAddressLine1] = useState((user as any)?.addressLine1 ?? "");
  const [city, setCity] = useState((user as any)?.city ?? "");
  const [state, setState] = useState((user as any)?.state ?? "");
  const [zipCode, setZipCode] = useState(user?.zipCode ?? "");
  const [intents, setIntents] = useState<string[]>([]);
  const [customIntent, setCustomIntent] = useState("");

  const totalSteps = 3;

  const toggleIntent = (name: string) =>
    setIntents((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );

  const addCustomIntent = () => {
    const name = customIntent.trim();
    if (!name) return;
    setIntents((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setCustomIntent("");
  };

  const saveProfile = async (fields: Record<string, string | undefined>) => {
    setSaving(true);
    try {
      const res = await fetch("/api/account/profile", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      queryClient.setQueryData(["/api/auth/user"], (prev: any) => ({
        ...prev,
        ...updated,
      }));
      return true;
    } catch (err: any) {
      toast({
        title: "Could not save",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const uploadProfileImage = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await fetch("/api/uploads/profile-image", {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!res.ok) throw new Error(await res.text().catch(() => "Upload failed"));
      const data = await res.json();
      if (data.imageUrl) {
        setProfileImageUrl(data.imageUrl);
        await saveProfile({ profileImageUrl: data.imageUrl });
      }
    } catch (err: any) {
      toast({
        title: "Upload failed",
        description: err?.message || "Try a smaller image.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const proceedFromStep2 = async () => {
    const fields: Record<string, string | undefined> = {};
    if (addressLine1.trim()) fields.addressLine1 = addressLine1.trim();
    if (city.trim()) fields.city = city.trim();
    if (state.trim()) fields.state = state.trim();
    if (zipCode.trim()) fields.zipCode = zipCode.trim();
    if (Object.keys(fields).length > 0) await saveProfile(fields);
    setShowPhotoPrompt(false);
    setStep(3);
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!displayName.trim()) {
        toast({ title: "Enter your business name", variant: "destructive" });
        return;
      }
      if (!sellerType) {
        toast({ title: "Select what you sell", variant: "destructive" });
        return;
      }
      const ok = await saveProfile({
        displayName: displayName.trim(),
        sellerType,
        bio: bio.trim() || undefined,
      });
      if (ok) setStep(2);
    } else if (step === 2) {
      // Photo and address both matter (a photo earns trust; an address is what
      // makes listings public and routes pickup). Nudge once if either is
      // missing, but don't hard-block — let them through if they insist.
      const hasFullAddress = Boolean(
        addressLine1.trim() && city.trim() && state.trim() && zipCode.trim(),
      );
      if (!profileImageUrl || !hasFullAddress) {
        setShowPhotoPrompt(true);
        return;
      }
      await proceedFromStep2();
    } else {
      // Anything they tapped becomes an on_request item, so a maker who has no
      // photos yet still lands on the map and can collect demand. Never block
      // the finish on this — a failure here shouldn't strand them in setup.
      if (intents.length > 0) {
        try {
          await fetch("/api/seller/intents", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ names: intents }),
          });
        } catch {
          /* non-fatal */
        }
      }
      // Final step drops them straight into the Quick Add listing flow rather
      // than the bare dashboard, so the first listing is the natural finish.
      setLocation("/seller?action=add");
    }
  };

  return (
    <div className="min-h-screen bg-muted/20 px-4 py-8 flex flex-col items-center">
      <div className="mb-6 flex items-center gap-2">
        {Array.from({ length: totalSteps }, (_, i) => {
          const s = i + 1;
          const done = s < step;
          const active = s === step;
          return (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition ${
                  done
                    ? "bg-primary text-primary-foreground"
                    : active
                      ? "border-2 border-primary text-primary"
                      : "border border-border text-muted-foreground"
                }`}
              >
                {done ? <Check className="h-4 w-4" /> : s}
              </div>
              {s < totalSteps && (
                <div className={`h-0.5 w-8 rounded ${done ? "bg-primary" : "bg-border"}`} />
              )}
            </div>
          );
        })}
      </div>

      <Card className="w-full max-w-lg shadow-lg">
        {step === 1 && (
          <CardContent className="p-6 space-y-5">
            <div>
              <h2 className="text-xl font-bold">Set up your seller profile</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                This is how buyers will find and recognize you on the marketplace.
              </p>
            </div>

            <div>
              <Label htmlFor="displayName">Business / farm name *</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Sunny Acre Farm"
                className="mt-1"
                maxLength={120}
                autoFocus
              />
            </div>

            <div>
              <Label>What do you sell? *</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {SELLER_TYPES.map((opt) => {
                  const selected = sellerType === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSellerType(opt.value)}
                      className={`rounded-lg border p-3 text-left transition ${
                        selected
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="bio">Short bio (optional)</Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Family-run farm growing heirloom tomatoes and herbs since 2018…"
                className="mt-1 min-h-[80px]"
                maxLength={1000}
              />
              <p className="mt-1 text-right text-[11px] text-muted-foreground">{bio.length} / 1000</p>
            </div>
          </CardContent>
        )}

        {step === 2 && (
          <CardContent className="p-6 space-y-5">
            <div>
              <h2 className="text-xl font-bold">Photo & pickup address</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Help buyers recognize you and know where to pick up.
              </p>
            </div>

            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="relative flex h-24 w-24 items-center justify-center rounded-full border-2 border-dashed border-border bg-muted/40 transition hover:border-primary/50 overflow-hidden"
              >
                {profileImageUrl ? (
                  <img src={profileImageUrl} alt="Profile" className="h-full w-full object-cover" />
                ) : uploading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                ) : (
                  <Camera className="h-6 w-6 text-muted-foreground" />
                )}
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="text-xs text-primary hover:underline"
              >
                {profileImageUrl ? "Change photo" : "Add a profile photo"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadProfileImage(file);
                  e.target.value = "";
                }}
              />
            </div>

            <div>
              <Label htmlFor="address">Pickup address</Label>
              <Input
                id="address"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                placeholder="123 Farm Road"
                className="mt-1"
                maxLength={200}
              />
            </div>

            <div className="grid grid-cols-5 gap-2">
              <div className="col-span-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Schodack"
                  className="mt-1"
                  maxLength={100}
                />
              </div>
              <div className="col-span-1">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="NY"
                  className="mt-1"
                  maxLength={2}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="zip">ZIP</Label>
                <Input
                  id="zip"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder="12033"
                  className="mt-1"
                  maxLength={10}
                />
              </div>
            </div>
          </CardContent>
        )}

        {step === 3 && (
          <CardContent className="p-6 flex flex-col items-center text-center gap-4 py-10">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Sprout className="h-8 w-8 text-primary" />
            </div>
            <div className="w-full">
              <h2 className="text-xl font-bold">You're all set, {displayName || "farmer"}!</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                What do you make or grow? Tap everything that applies.
              </p>

              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {INTENT_SUGGESTIONS.map((name) => {
                  const on = intents.includes(name);
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => toggleIntent(name)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                        on
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background text-muted-foreground"
                      }`}
                      data-testid={`chip-intent-${name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {name}
                    </button>
                  );
                })}
                {intents
                  .filter((n) => !INTENT_SUGGESTIONS.includes(n))
                  .map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => toggleIntent(name)}
                      className="rounded-full border border-primary bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
                    >
                      {name}
                    </button>
                  ))}
              </div>

              <div className="mt-3 flex gap-2">
                <Input
                  value={customIntent}
                  onChange={(e) => setCustomIntent(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomIntent();
                    }
                  }}
                  placeholder="Something else you sell"
                  className="h-9 text-sm"
                  maxLength={60}
                />
                <Button type="button" variant="outline" size="sm" onClick={addCustomIntent}>
                  Add
                </Button>
              </div>

              <p className="mt-3 rounded-lg bg-primary/5 px-3 py-2 text-left text-xs text-muted-foreground">
                These put you on the map right away and let neighbors ask for them.
                They stay off the browse page until you add a photo and price, so
                nobody can order something you haven't listed yet.
              </p>
            </div>
          </CardContent>
        )}

        <div className="border-t px-6 py-4 flex items-center justify-between">
          {step > 1 && step < 3 ? (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          ) : (
            <div />
          )}
          <Button type="button" onClick={handleNext} disabled={saving || uploading}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : step === 3 ? (
              <>
                <Camera className="mr-2 h-4 w-4" />
                Add my first listing
              </>
            ) : (
              <>
                Continue
                <ChevronRight className="ml-1 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </Card>

      <button
        type="button"
        onClick={() => setLocation("/seller")}
        className="mt-4 text-xs text-muted-foreground hover:underline"
      >
        {step === 3 ? "I'll add my first listing later" : "Skip for now — I'll set this up later"}
      </button>

      <Dialog open={showPhotoPrompt} onOpenChange={setShowPhotoPrompt}>
        <DialogContent className="w-[90vw] max-w-sm">
          <DialogHeader>
            <DialogTitle>Before you finish</DialogTitle>
            <DialogDescription>
              Listings only go public once you add an address (it stays private
              until someone orders). A photo helps buyers trust you, too.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button type="button" onClick={() => setShowPhotoPrompt(false)}>
              Go back
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={proceedFromStep2}
              disabled={saving}
            >
              Continue without
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
